import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack, Print } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetOrderQuery } from '../services/orderApi';
import ThermalReceipt80mm from '../components/pos/receipt/ThermalReceipt80mm';
import { mapOrderToReceipt } from '../components/pos/receipt/mapOrderToReceipt';
import { useGetReceiptTemplateQuery } from '../services/receiptTemplateApi';
import { usePrinterContext } from '../context/PrinterContext';

const PrintReceiptPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isFetching, isError } = useGetOrderQuery(orderId, { skip: !orderId });
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();
  const receipt = useMemo(() => mapOrderToReceipt(data, receiptTemplate), [data, receiptTemplate]);
  const searchParams = new URLSearchParams(location.search || '');
  const shouldAutoPrint = location.state?.autoPrint === true || searchParams.get('autoprint') === '1';

  const { isConnected, silentPrintOrder } = usePrinterContext();
  // Ref keeps latest printer state accessible inside the setTimeout closure
  const printerRef = useRef(null);
  printerRef.current = { isConnected, silentPrintOrder };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = receipt ? `Receipt ${receipt.billNumber}` : 'Print Receipt';
    return () => {
      document.title = previousTitle;
    };
  }, [receipt]);

  useEffect(() => {
    if (!receipt || !shouldAutoPrint) return;
    const timer = window.setTimeout(async () => {
      const { isConnected: connected, silentPrintOrder: silentPrint } = printerRef.current;
      if (connected) {
        try {
          await silentPrint(receipt);
          return;
        } catch {
          // companion error — fall through to browser print
        }
      }
      window.print();
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [receipt, shouldAutoPrint]); // printerRef used inside — no dep needed

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E2E8F0', p: { xs: 1.5, sm: 2.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ textTransform: 'none' }}>
          Back
        </Button>
        <Button startIcon={<Print />} variant="contained" onClick={() => window.print()} disabled={!receipt} sx={{ textTransform: 'none' }}>
          Print Receipt
        </Button>
      </Stack>

      <Paper sx={{ maxWidth: 'fit-content', mx: 'auto', p: { xs: 1, sm: 2 }, borderRadius: '16px' }}>
        {isFetching ? (
          <Stack alignItems="center" spacing={1} sx={{ width: '80mm', py: 8 }}>
            <CircularProgress size={24} />
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>Loading receipt...</Typography>
          </Stack>
        ) : isError || !receipt ? (
          <Stack alignItems="center" spacing={1} sx={{ width: '80mm', py: 8 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#DC2626', fontWeight: 700 }}>Unable to load receipt.</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', textAlign: 'center' }}>
              Go back to POS and try printing again.
            </Typography>
          </Stack>
        ) : (
          <ThermalReceipt80mm receipt={receipt} />
        )}
      </Paper>
    </Box>
  );
};

export default PrintReceiptPage;
